const {Component} = require("react");
const store = require("stores/UserStore");
const Nevermore = require("components/backend/calendar/Nevermore");
const {decodeObservable} = require("helpers/object");
const moment = require("moment");
const {classList} = require("helpers/html");
const Promise = require("es6-promise");
const SlotEditorForm = require("components/backend/calendar/SlotEditorForm");
const env = require("services/env");
const assign = require("object-assign");
const {toSameDayOfWeek, intervalsOverlap, inInterval} = require("helpers/moment");
const FooterInformation = require("components/backend/calendar/plugins/FooterInformation");
const ItemActions = require("components/backend/calendar/plugins/ItemActions");
const MobileActions = require("components/backend/calendar/plugins/MobileActions");
const ItemClass = require("components/backend/calendar/plugins/ItemClass");
const Load = require("components/mixins/Load");
const Confirm = require("components/modals/Confirm");
const Screen = require("services/screen");
const {repeaterWith} = require("nevermorejs/lib/item");
const BeforeUnload = require("helpers/BeforeUnload");

function itemFrom(item, weekDate) {
	return item.repeats ? item.weekFrom(weekDate) : item.from;
}

function itemTo(item, weekDate) {
	return item.repeats ? item.weekTo(weekDate) : item.to;
}

class Calendar extends Component {

	constructor(props) {
		super(props);

		this.state = {
			items: decodeObservable(store.get("slots", [])),
			loading: false,
			currentItem: null,
			editing: false,
			message: null,
		};

		this.handleItemSave = null;
		this.handleItemDelete = null;
		this.handleItemAddException = null;
		this.handleItemCancel = null;
	}

	get className() {
		return classList(
			"profile-calendar",
			this.props.className,
			this.state.loading ? "loading" : false,
			this.state.editing ? "editing" : false
		);
	}

	onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState({loading: true, message: null});

		store.save({slots: this.state.value}).then(() => {
			this.setState({
				loading: false,
				value: decodeObservable(store.get("slots", [])),
				message: "Dine indstillinger er blevet gemt."
			}, () => {
				this.planner.replaceValue(this.state.value);
				BeforeUnload.clear();
			});
		});
	};

	handleChange = (value) => {
		this.setState({value});
		BeforeUnload.set();
	};

	stopEditing() {
		this.setState({editing: false});
	}

	startEditing(item) {
		this.setState({currentItem: item, editing: true});
	}

	addMiddleware = (payload) => {
		if (payload.origin === "drag-create") {
			return new Promise((resolve, reject) => {
				this.handleItemSave = (item, changes) => {
					payload.item = assign(this.state.currentItem, changes);
					resolve(payload);

					this.stopEditing();
					BeforeUnload.set();
				};

				this.handleItemCancel = () => {
					this.stopEditing();
					reject();
				};

				this.startEditing(payload.item);
			});
		}
	};

	removeMiddleware = (payload) => {
		return this.confirm.await(
			payload.item.repeats ?
				"Er du sikker på at du vil fjerne denne og alle gentagende tider?" :
				"Er du sikker på at du vil fjerne denne tid?"
		);
	};

	changeMiddleware = (payload) => {
		// Adding a repeater exception
		if (payload.origin === "repeater-add-exception") {
			return this.confirm.await("Er du sikker på at du vil fjerne denne tid?");
		}

		return payload;
	};

	getPlannerDate = () => {
		return this.planner.instance.getDate();
	};

	getItemCanRepeat = (item, changes) => {
		const date = this.getPlannerDate();
		const simItem = item.clone(changes, {repeater: true});
		let sibling;

		for (let i = 0; i < this.planner.instance.items.length; i++) {
			sibling = this.planner.instance.items[i];

			if ((item === sibling) || !sibling.repeats) {
				continue;
			}

			if (intervalsOverlap(
					simItem.weekFrom(date),
					simItem.weekTo(date),
					sibling.weekFrom(date),
					sibling.weekTo(date)
				)) {
				return false;
			}
		}

		return true;
	};

	getTimeIntervals = (item, changes) => {
		const simItem = item.clone(changes);
		const date = this.getPlannerDate();

		if (simItem.repeats && !simItem.repeatsOn(date)) {
			return [];
		}

		const from = itemFrom(simItem, date);
		const to = itemTo(simItem, date);

		const start = from.clone().startOf("day").add(this.planner.instance.options.minTime, "hour");
		const end = from.clone().startOf("day").add(this.planner.instance.options.maxTime, "hour");

		const siblings = this.planner.instance.items.filter(sibling => {
			if (sibling.repeats && !sibling.repeatsOn(date)) {
				return false;
			}

			return (item !== sibling) && inInterval(itemFrom(sibling, date), start, end);
		});

		const intervals = [];
		let currentInterval = {};
		let currentTime = start.clone();
		let collisions;

		const addInterval = () => {
			if (currentInterval.to.diff(currentInterval.from, "hour") >= 1) {
				intervals.push(currentInterval);
			}

			currentInterval = {};
		};

		while (currentTime.isBefore(end)) {

			collisions = siblings.filter(
				sibling => inInterval(currentTime, itemFrom(sibling, date), itemTo(sibling, date), false)
			);

			if (currentInterval.from == null) {
				if (collisions.length === 0) {
					currentInterval.from = currentTime.clone();
				}
			}
			else {
				if (collisions.length > 0) {
					currentInterval.to = currentTime.clone();
					addInterval();
				}
			}

			currentTime.add(30, "minutes");
		}

		if (currentInterval.from && (currentInterval.to == undefined)) {
			currentInterval.to = end.clone();
			addInterval();
		}

		return intervals;
	};

	isExclusionRemovable = (item, changes, date) => {
		const simItem = item.clone(changes);

		if (!simItem.repeats) {
			return false;
		}

		let sibling;

		for (let i = 0; i < this.planner.instance.items.length; i++) {
			sibling = this.planner.instance.items[i];

			if ((sibling === item) ||
				(sibling.repeats && !sibling.repeatsOn(date)) ||
				(sibling.from.day() !== simItem.from.day())) {

				continue;
			}

			if (intervalsOverlap(
					itemFrom(simItem, date), itemTo(simItem, date),
					itemFrom(sibling, date), itemTo(sibling, date)
				)) {
				return false;
			}
		}

		return true;
	};

	get nevermorePlugins() {
		if (Screen.desktop()) return [FooterInformation, ItemActions, ItemClass];

		// Mobile
		return [MobileActions, FooterInformation];
	}

	componentDidMount() {
		// Hook to item edit
		this.planner.instance.on("itemEdit", item => {
			this.handleItemSave = (item, changes) => {
				this.planner.instance.updateItem(item, changes);
				this.stopEditing();
				BeforeUnload.set();
			};

			this.handleItemDelete = (item, changes) => {
				this.planner.instance.removeItem(item);
				this.stopEditing();
			};

			this.handleItemAddException = (item, changes) => {
				changes = {
					repeater: repeaterWith(
						item.repeater,
						item.weekFrom(this.planner.instance.getDate())
					)
				};
				this.planner.instance.updateItem(item, changes);
				//this.stopEditing();
				BeforeUnload.set();
			};

			this.handleItemCancel = () => {
				this.stopEditing();
			};

			this.startEditing(item);
		});
	}

	renderMessage() {
		if (this.state.message) {
			return (<div className="message">
				{this.state.message}
				<div className="checkmark-container">
					<svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
						<circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
						<path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
					</svg>
				</div>
			</div>);
		}
	}

	render() {
		return (
			<form className={this.className} onSubmit={this.onSubmit}>
				<div className="top-bar">
					<h1>Vælg hvornår du vil undervise</h1>
					<button type="submit" className="btn btn-primary">Gem
						<Load></Load>
					</button>
				</div>
				{this.renderMessage()}
				<div className="calendar-info">
					<div className="calendar-info-block">
						<span className="prim"></span>
						<p>Gentagende tider</p>
					</div>
					<div className="calendar-info-block">
						<span className="second"></span>
						<p>Engangstider</p>
					</div>
				</div>

				<Nevermore ref={el => this.planner = el} value={this.state.items}
									 snap={60}
									 minTime={6}
									 plugins={this.nevermorePlugins}
									 handleChange={this.handleChange}
									 addMiddleware={this.addMiddleware}
									 removeMiddleware={this.removeMiddleware}
									 changeMiddleware={this.changeMiddleware}
									 minDate={moment().startOf("week")}/>

				<SlotEditorForm item={this.state.currentItem}
												minDate={moment().startOf("week")}
												getDate={this.getPlannerDate}
												getTimeIntervals={this.getTimeIntervals}
												isExclusionRemovable={this.isExclusionRemovable}
												canRepeat={this.getItemCanRepeat}
												handleSave={this.handleItemSave}
												handleDelete={this.handleItemDelete}
												handleAddException={this.handleItemAddException}
												handleCancel={this.handleItemCancel}/>

				<Confirm ref={el => this.confirm = el}/>
			</form>
		);
	}

}

module.exports = Calendar;



// WEBPACK FOOTER //
// ./resources/js/components/backend/calendar/Calendar.js